import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Badge, Box, Button, Flex, SimpleGrid, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { doGetAvailableRewards, doGetLoyaltyTier, doGetPointHistory, doGetRedeemHistory, doTriggerRedeem } from '../../../../redux/onlineSales/onlineSales.actions';

export const AvailableRewardsList = ({ rewards, totalPoints }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();
    const [selectedReward, setSelectedReward] = useState(null);

    if (!rewards || rewards.length === 0) {
        return null;
    }

    const onOpenConfirm = (voucher) => {
        setSelectedReward(voucher);
        onOpen();
    };

    const handleRedeem = () => {
        if (!selectedReward) return;

        const payload = {
            rewardID: selectedReward.id,
        };

        dispatch(
            doTriggerRedeem(payload, () => {
                toast({
                    title: 'Reward redeemed successfully!',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
                dispatch(doGetAvailableRewards());
                dispatch(doGetLoyaltyTier());
                dispatch(doGetPointHistory());
                dispatch(doGetRedeemHistory());
                onClose();
            }),
        );
    };

    return (
        <Box w='full' mt={2}>
            <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            Confirm Reward
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to use <strong>{selectedReward?.pointsRequired} points</strong> to get{' '}
                            <i>
                                <strong>{selectedReward?.title}</strong>
                            </i>
                            ?
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose} borderRadius='full'>
                                Cancel
                            </Button>
                            <Button colorScheme='green' onClick={handleRedeem} ml={3} borderRadius='full'>
                                Yes, Get Reward.
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <Text fontSize='lg' fontWeight='bold' color='gray.800' mb={4}>
                Available Rewards
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
                {rewards.map((voucher) => {
                    const canAfford = totalPoints >= voucher.pointsRequired;

                    return (
                        <Flex key={voucher.id} direction='column' bg='white' p={5} borderRadius='xl' border='1px' borderColor='gray.100' boxShadow='sm' opacity={canAfford ? 1 : 0.7} transition='all 0.2s' _hover={{ transform: 'translateY(-4px)', boxShadow: 'md', borderColor: 'red.200' }}>
                            <Flex justify='space-between' align='start' mb={3}>
                                <Box fontSize='3xl' bg='gray.50' p={2} borderRadius='lg'>
                                    {voucher.image || '🎁'}
                                </Box>
                                <Badge colorScheme={canAfford ? 'green' : 'gray'} borderRadius='full' px={3} py={1} fontSize='xs' fontWeight='bold'>
                                    {voucher.pointsRequired} pts
                                </Badge>
                            </Flex>

                            <Text fontWeight='bold' color='gray.800' mb={2} fontSize='md' noOfLines={1}>
                                {voucher.title}
                            </Text>

                            <Text fontSize='sm' color='gray.500' mb={4} flex={1} noOfLines={2} textOverflow='ellipsis'>
                                {voucher.description}
                            </Text>

                            <Box mt='auto' pt={3} borderTop='1px dashed' borderColor='gray.100'>
                                {canAfford ? (
                                    <Button w='full' size='sm' colorScheme='green' borderRadius='full' onClick={() => onOpenConfirm(voucher)}>
                                        Get Rewards
                                    </Button>
                                ) : (
                                    <Text fontSize='xs' color={canAfford ? 'green.500' : 'red.400'} fontWeight='semibold' textAlign='center'>
                                        {canAfford ? '' : `Need ${voucher.pointsRequired - totalPoints} more pts`}
                                    </Text>
                                )}
                            </Box>
                        </Flex>
                    );
                })}
            </SimpleGrid>
        </Box>
    );
};
